/**
 * IngredientsAndTagsSection component for the dish form
 */

import React, { useState, useEffect } from 'react';
import Textarea from '../comman/textarea.js';
import CustomSelect from '../comman/select.js';
import AdditionsMultiSelect from '../comman/additions-multi-select.js';
import { getTranslation } from '../../utils/translate.js';
import { fetchDishTags, fetchDishCuisineTags } from '../../services/menu-service.js';
import { formatTags } from '../../utils/dishFormUtils.js';

// Module-level cache to prevent duplicate API calls across component remounts
const tagsCache = {
  dishTags: null,
  cuisineTags: null,
  isFetchingDishTags: false,
  isFetchingCuisineTags: false
};

/**
 * Spacing constants for consistent layout
 * Using 8px increments (0.5rem = 8px in Tailwind)
 */
const SPACING = {
  // Typography and section styles
  SECTION_TITLE: 'text-xl font-bold mb-4 border-b pb-2 text-gray-800 dark:text-[#c9d1d9] border-gray-200 dark:border-[#30363d]',

  // Layout styles
  GRID: 'grid grid-cols-1 md:grid-cols-2 gap-4 mt-4',
  FIELD_WRAPPER: 'relative',

  // Additions styles
  ADDITIONS_CONTAINER: 'mt-3 space-y-2',
  ADDITION_ITEM: 'flex items-center justify-between gap-2 mt-2 p-2 border rounded-md bg-white dark:bg-[#0d1117] border-gray-200 dark:border-[#30363d]',
  ADDITION_LABEL: 'text-sm flex-grow text-gray-900 dark:text-[#c9d1d9]',
  ADDITION_PRICE_WRAPPER: 'flex items-center gap-1',
  ADDITION_PRICE_LABEL: 'text-xs text-gray-500 dark:text-[#8b949e]',
  ADDITION_PRICE_INPUT: 'w-20 px-2 py-1 border border-gray-300 dark:border-[#30363d] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]',
};

/**
 * Renders a selected addition item with its price input
 * @param {Object} addition - The addition object
 * @param {Function} handleAdditionPriceOverrideChange - Handler for price override changes
 * @param {string} language - Current language
 * @returns {JSX.Element|null} The rendered addition item or null if invalid
 */
const SelectedAdditionWithPrice = ({ addition, handleAdditionPriceOverrideChange, language }) => {
  // Ensure addition and addition.price exist
  if (!addition || typeof addition.price !== 'number') {
    console.warn("SelectedAdditionWithPrice called with invalid addition:", addition);
    return null;
  }

  return (
    <div 
      key={addition.id}
      className={SPACING.ADDITION_ITEM}
    >
      <span className={SPACING.ADDITION_LABEL}>{addition.nameHy || `ID: ${addition.id}`}</span>
      <div className={SPACING.ADDITION_PRICE_WRAPPER}>
        <label htmlFor={`addition-price-${addition.id}`} className={SPACING.ADDITION_PRICE_LABEL}>
          {getTranslation('menu.changePrice', language) || 'Change price'} (AMD)
        </label>
        <input
          id={`addition-price-${addition.id}`}
          name={`addition-price-${addition.id}`}
          type="text"
          value={addition.overridedPrice !== null ? addition.overridedPrice : addition.price}
          onChange={(e) => handleAdditionPriceOverrideChange(addition.id, e.target.value)}
          className={SPACING.ADDITION_PRICE_INPUT}
          autoComplete="off"
          aria-label={`Price for ${addition.nameHy || 'addition'}`}
        />
      </div>
    </div>
  );
};

/**
 * IngredientsAndTagsSection component
 * @param {Object} props - Component props
 * @returns {JSX.Element} IngredientsAndTagsSection component
 */
const IngredientsAndTagsSection = ({
  formData,
  errors,
  cuisineTagOptions: propsCuisineTagOptions,
  dishTagOptions: propsDishTagOptions,
  ungroupedAdditionOptions,
  selectedUngroupedValues,
  handleTranslationChange,
  handleTranslateIngredients,
  isTranslating,
  handleSingleSelectChange,
  handleMultiSelectChange,
  handleUngroupedAdditionsChange,
  handleAdditionPriceOverrideChange,
  handleAdditionsModalOpen,
  language,
  setErrors
}) => {
  // State for fetched data - initialize from cache if available to prevent race condition
  const [fetchedDishTags, setFetchedDishTags] = useState(() => {
    // Initialize from cache if available (synchronous)
    if (!propsDishTagOptions || propsDishTagOptions.length === 0) {
      return tagsCache.dishTags || [];
    }
    return [];
  });
  const [fetchedCuisineTags, setFetchedCuisineTags] = useState(() => {
    // Initialize from cache if available (synchronous)
    if (!propsCuisineTagOptions || propsCuisineTagOptions.length === 0) {
      return tagsCache.cuisineTags || [];
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState({
    dishTags: false,
    cuisineTags: false
  });

  // Fetch data on component mount (only if not already in cache/state)
  useEffect(() => {
    const fetchTagsData = async () => {
      try {
        // Fetch dish tags if not provided from props and not already loaded
        if ((!propsDishTagOptions || propsDishTagOptions.length === 0) && 
            (!fetchedDishTags || fetchedDishTags.length === 0)) {
          // Skip if already fetching or already cached (state initialized from cache)
          if (tagsCache.isFetchingDishTags || tagsCache.dishTags) return;
          
          tagsCache.isFetchingDishTags = true;
          setIsLoading(prev => ({ ...prev, dishTags: true }));
          try {
            const dishTagsData = await fetchDishTags();
            if (dishTagsData) {
              tagsCache.dishTags = dishTagsData;
              setFetchedDishTags(dishTagsData);
            }
          } finally {
            setIsLoading(prev => ({ ...prev, dishTags: false }));
            tagsCache.isFetchingDishTags = false;
          }
        }

        // Fetch cuisine tags if not provided from props and not already loaded
        if ((!propsCuisineTagOptions || propsCuisineTagOptions.length === 0) && 
            (!fetchedCuisineTags || fetchedCuisineTags.length === 0)) {
          // Skip if already fetching or already cached (state initialized from cache)
          if (tagsCache.isFetchingCuisineTags || tagsCache.cuisineTags) return;
          
          tagsCache.isFetchingCuisineTags = true;
          setIsLoading(prev => ({ ...prev, cuisineTags: true }));
          try {
            const cuisineTagsData = await fetchDishCuisineTags();
            if (cuisineTagsData) {
              tagsCache.cuisineTags = cuisineTagsData;
              setFetchedCuisineTags(cuisineTagsData);
            }
          } finally {
            setIsLoading(prev => ({ ...prev, cuisineTags: false }));
            tagsCache.isFetchingCuisineTags = false;
          }
        }
      } catch (error) {
        console.error("Error fetching tags data:", error);
        setIsLoading({
          dishTags: false,
          cuisineTags: false
        });
        // Reset cache flags on error
        tagsCache.isFetchingDishTags = false;
        tagsCache.isFetchingCuisineTags = false;
      }
    };

    fetchTagsData();
  }, [propsDishTagOptions, propsCuisineTagOptions, fetchedDishTags, fetchedCuisineTags]);

  // Prepare dish tag options
  const dishTagSelectOptions = React.useMemo(() => {
    if (propsDishTagOptions && propsDishTagOptions.length > 0) {
      return propsDishTagOptions;
    }

    if (fetchedDishTags && fetchedDishTags.length > 0) {
      return formatTags(fetchedDishTags, 'tag');
    }

    return [];
  }, [propsDishTagOptions, fetchedDishTags]);

  // Prepare cuisine tag options
  const cuisineTagSelectOptions = React.useMemo(() => {
    if (propsCuisineTagOptions && propsCuisineTagOptions.length > 0) {
      return propsCuisineTagOptions;
    }

    if (fetchedCuisineTags && fetchedCuisineTags.length > 0) {
      return formatTags(fetchedCuisineTags, 'cuisineTag');
    }

    return [];
  }, [propsCuisineTagOptions, fetchedCuisineTags]);

  return (
    <div>
      <h2 className={SPACING.SECTION_TITLE}>
        {getTranslation('menu.ingredientsAndTags', language)}
      </h2>
      <div className={SPACING.GRID}>
        {/* Ingredients fields - Armenian, Russian, English */}
        <div className={SPACING.FIELD_WRAPPER}>
          <Textarea 
            isRequired={true} 
            key="hy"
            label={getTranslation('menu.ingredientsArmenian', language)}
            labelFor="ingredients-hy"
            placeholder={getTranslation('menu.ingredientsPlaceholder', language)}
            value={formData.ingridientsDto[0]?.ingredientHy || ""}
            onChange={(e) => handleTranslationChange(e.target.value, 'ingredientHy')}
            error={errors?.ingredientHy}
          />
        </div>
        <div className={SPACING.FIELD_WRAPPER}>
          <Textarea 
            isRequired={false} 
            key="ru"
            label={getTranslation('menu.ingredientsRussian', language) || 'Ingredients (Russian)'}
            labelFor="ingredients-ru"
            placeholder={getTranslation('menu.ingredientsPlaceholder', language)}
            value={formData.ingridientsDto[0]?.ingredientRu || ""}
            onChange={(e) => handleTranslationChange(e.target.value, 'ingredientRu')}
            error={errors?.ingredientRu}
          />
        </div>
        <div className={SPACING.FIELD_WRAPPER}>
          <Textarea 
            isRequired={false} 
            key="en"
            label={getTranslation('menu.ingredientsEnglish', language) || 'Ingredients (English)'}
            labelFor="ingredients-en"
            placeholder={getTranslation('menu.ingredientsPlaceholder', language)}
            value={formData.ingridientsDto[0]?.ingredientEn || ""}
            onChange={(e) => handleTranslationChange(e.target.value, 'ingredientEn')}
            error={errors?.ingredientEn}
          />
        </div>

        {/* Translate Ingredients Button */}
        {handleTranslateIngredients && (
          <div className={SPACING.FIELD_WRAPPER}>
            <button
              type="button"
              onClick={handleTranslateIngredients}
              disabled={isTranslating || !formData.ingridientsDto[0]?.ingredientHy || !formData.ingridientsDto[0]?.ingredientHy.trim()}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isTranslating ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {getTranslation('menu.translating', language) || 'Translating...'}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                  {getTranslation('menu.translate', language) || 'Translate'}
                </>
              )}
            </button>
          </div>
        )}

        {/* Cuisine tag field */}
        <div className={SPACING.FIELD_WRAPPER}>
          <CustomSelect
            fieldName="dishCuisineTagDto"
            name="dishCuisineTagDto"
            isRequired={true}
            onChange={(selectedOption) => handleSingleSelectChange(selectedOption, "dishCuisineTagDto")}
            value={formData.dishCuisineTagDto?.id ? Number(formData.dishCuisineTagDto.id) : null} // Ensure number type for ID matching
            label={getTranslation('menu.cuisineTag', language)}
            options={cuisineTagSelectOptions.map(opt => ({
              ...opt,
              value: Number(opt.value) // Ensure value is number for proper matching
            }))}
            placeholder={getTranslation('menu.selectCuisineTag', language)}
            error={errors?.dishCuisineTagDto}
            setError={(f, m) => setErrors(p => ({...p, [f]: m}))}
            isLoading={isLoading.cuisineTags}
          />
        </div>

        {/* Dish tags field */}
        <div className={SPACING.FIELD_WRAPPER}>
          <CustomSelect
            onChange={(selectedOption) => handleSingleSelectChange(selectedOption, "dishTagDto")}
            value={formData.dishTagDto?.id ? Number(formData.dishTagDto.id) : null} // Ensure number type for ID matching
            name="dishTagDto"
            fieldName="dishTagDto"
            error={errors?.dishTagDto}
            setError={(f, m) => setErrors(p => ({...p, [f]: m}))}
            isRequired={true}
            label={getTranslation('menu.dishTags', language)}
            options={dishTagSelectOptions.map(opt => ({
              ...opt,
              value: Number(opt.value) // Ensure value is number for proper matching
            }))}
            placeholder={getTranslation('menu.selectTag', language)}
            isLoading={isLoading.dishTags}
          />
        </div>

        {/* Additions field */}
        <div className={SPACING.FIELD_WRAPPER}>
          <AdditionsMultiSelect
            name="ungroupedDishAdditionDtoList"
            label={getTranslation('menu.additions', language)}
            placeholder={getTranslation('menu.additions', language)}
            options={ungroupedAdditionOptions}
            value={selectedUngroupedValues.map(add => ({value: add.value, label: add.label}))}
            onChange={handleUngroupedAdditionsChange}
            onAddClick={() => handleAdditionsModalOpen()}
          />

          {/* Render price overrides for selected ungrouped additions */}
          <div className={SPACING.ADDITIONS_CONTAINER}>
            {selectedUngroupedValues.map(addition => (
              <SelectedAdditionWithPrice 
                key={addition.value}
                addition={formData.dishAdditionDtoList.find(a => a.id === addition.value)}
                handleAdditionPriceOverrideChange={handleAdditionPriceOverrideChange}
                language={language}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IngredientsAndTagsSection;
